(ns app.domain.project.model
  (:require #?(:clj [core.authorization.core :as auth])
            [app.domain.group.config.model :as config-model]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [clojure.set :as set]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]))

(def strong-terminal-states
  "These states mean that the workspace is in an inactive state
  regardless of the state of other workspace children"
  {:entity.type/pre-consultation #{:pre-consultation.state/withdrawn
                                   :pre-consultation.state/rejected
                                   :pre-consultation.state/expired}
   :entity.type/application      #{:state.application/withdrawn
                                   :state.application/rejected
                                   :state.application/refused
                                   :state.application/denied}})

(def successful-terminal-states
  {:entity.type/application     #{:state.application/permit-issued
                                  :state.application/license-issued
                                  :state.application/registration-issued
                                  :state.application/approved
                                  :state.application/approved-in-principle
                                  :state.application/archived}
   :entity.type/execution       #{:execution.state/finished
                                  :execution.state/archived
                                  :execution.state/dormancy
                                  :execution.state/revoked
                                  :execution.state/closed
                                  :execution.state/closed-code-enforcement
                                  :execution.state/complete}
   :entity.type/periodic-parent #{:periodic-parent.state/finished
                                  :periodic-parent.state/archived}
   :entity.type/archiving       #{:archiving.state/archived}})

(def permit-types-without-project
  "Permit types that skip project"
  #{:permit-type/L
    :permit-type/LS
    :permit-type/CR
    :permit-type/PWO})

(def terminal-states
  "If all the children on a workspace are in one of these states,
  the workspace is considered inactive and a project can be closed"
  (merge-with set/union
              strong-terminal-states
              successful-terminal-states))


(defn in-state? [states {entity-type :entity/type state :domain/state}]
  (-> states
      (get entity-type)
      (contains? state)))

(defn land-use-process-flow?
  "Whether the workspace uses the land use process flow: the Land Use permit type unless it is zoning, or Planning
   configured with the land use process flow. Mirrors `app.process.group/fsm-def-for-target`."
  [workspace]
  (let [process-flow (config-model/resolve-value :group.config/process-flow workspace)]
    (case (:workspace/permit-type workspace)
      :permit-type/LU (not= :process-flow/zoning process-flow)
      :permit-type/P  (= :process-flow/land-use process-flow)
      false)))

(defn- in-land-use-terminal-state?
  "Land use Finished concludes a refusal or denial regardless of pending workspace children.
   In other flows the workspace carries on in the execution phase."
  [{entity-type :entity/type state :domain/state workspace :entity/parent}]
  (and (= :entity.type/application entity-type)
       (= :state.application/finished state)
       (land-use-process-flow? workspace)))

(def in-strong-terminal-state?
  (some-fn (partial in-state? strong-terminal-states) in-land-use-terminal-state?))

(def in-terminal-state?
  (some-fn (partial in-state? terminal-states) in-land-use-terminal-state?))

(defn application-in-progress?
  "Whether an application has advanced beyond draft without reaching a terminal state."
  [{:domain/keys [state] :as application}]
  (and state
       (not= :state.application/draft state)
       (not (in-terminal-state? application))))

(defn pre-consultation-in-terminal-state-or-none?
  "A completed pre-consultation is only in terminal state if there also is an application."
  [workspace]
  (if-let [{pre-con-state :domain/state} (workspace-model/workspace-pre-consultation workspace)]
    (or (contains? #{:pre-consultation.state/finished
                     :pre-consultation.state/archived
                     :pre-consultation.state/not-applicable}
                   pre-con-state)
        (and (= :pre-consultation.state/completed pre-con-state)
             (boolean (workspace-model/workspace-application workspace))))
    true))

(defn ws-children-sans-pre-consultation
  "Handle pre-consultation separately from other phase entities."
  [workspace]
  (->> (workspace-model/phase-entities workspace)
       (remove (eu/is-type-fn :entity.type/pre-consultation))))

(defn- workspace-inactive-for-children [workspace ws-children]
  (or (some in-strong-terminal-state? ws-children)
      (and (every? in-terminal-state? ws-children)
           (pre-consultation-in-terminal-state-or-none? workspace))))

(defn workspace-inactive? [workspace]
  (let [ws-children (ws-children-sans-pre-consultation workspace)]
    (workspace-inactive-for-children workspace ws-children)))

(defn workspace-inactive-or-in-archival? [workspace]
  (let [ws-children (->> (ws-children-sans-pre-consultation workspace)
                         (remove (eu/is-type-fn :entity.type/archiving)))]
    (workspace-inactive-for-children workspace ws-children)))


(defn ws-children-can-be-archived? [workspace]
  (let [ws-children (->> workspace
                         workspace-model/phase-entities
                         (remove (eu/is-type-fn :entity.type/archiving)))]
    (or (some in-strong-terminal-state? ws-children)
        (and (every? in-terminal-state? (->> ws-children
                                             (remove (eu/is-type-fn :entity.type/pre-consultation))))
             (pre-consultation-in-terminal-state-or-none? workspace)))))


(defn user-has-role? [#?(:clj user :cljs _) project role]
  #?(:clj (auth/has-role? project user role)
     :cljs (-> project :granted-permissions set role)))

(defn can-close? [user project]
  (and (du/in-state? :project.state/active project)
       (authority/authority-in-municipality? user
                                             (:project/municipality project))
       (user-has-role? user project :role.project/writer)
       (->> project
            :entity/_parent
            (every? workspace-inactive?))))

(defn can-reopen? [user project]
  (and (du/in-state? :project.state/closed project)
       (authority/authority-in-municipality? user
                                             (:project/municipality project))
       (user-has-role? user project :role.project/writer)))

(def ^{:arglists '([project])} project-workspaces
  (child/child-entities-of-type-fn :entity.type/workspace))
